import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  DatePicker,
  Button,
  Input,
  Select,
  Modal,
  Space,
  notification,
  Card,
  Table,
  List,
  Checkbox,
  Badge,
  Form,
} from 'antd';
import {
  orderPolygonDetail,
  orderMapDetail,
  orderPolygonSave,
  orderPolygonEdit,
  orderPolygonDelete,
  orderPolygonParameter,
  orderMapStoresPolygon,
} from '../../../../store/requests/order';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import moment from 'moment';
import OrderAnalysisMap from './OrderAnalysisMap';
import StoreDetailsFilter from './StoreDetailsFilter';
import ExportOrderDetailData from './ExportOrderDetailData';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OrderAnalysis = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [selectedDates, setSelectedDates] = useState([]);
  const [polygonPath, setPolygonPath] = useState([]);
  const [map, setMap] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [polygonName, setPolygonName] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editPolygonName, setEditPolygonName] = useState('');
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState([]);
  const prevPayloadRef = useRef(null);
  const [maxDays, setMaxDays] = useState(21);
  const [drawing, setDrawing] = useState(true);
  const [filters, setFilters] = useState({
    IS_CANCELLED_ORDER: false,
    IS_RETURNED_ORDER: false,
    IS_NOT_ON_TIME_ORDER: false,
    IS_ON_TIME_ORDER: false,
    IS_NOT_RETURNED_ORDER: false,
  });
  const [showAllStores, setShowAllStores] = useState(false);
  const [allStoresData, setAllStoresData] = useState([]);
  const [allStoresMap, setAllStoresMap] = useState([]);
  const [includeDeliveryFromStore, setIncludeDeliveryFromStore] =
    useState(true);
  const [isStoreFilterOpen, setIsStoreFilterOpen] = useState(false);
  const [storeFilters, setStoreFilters] = useState({});
  const [storeFilterForm] = Form.useForm();
  const storeFiltersRef = useRef({});

  useEffect(() => {
    const fetchOrderPolygonDate = async () => {
      const response = await orderPolygonParameter('order_date_range_week');
      const weeks = response?.content?.[0]?.value || 3;
      const days = Number(weeks) * 7;
      setMaxDays(days);
    };
    fetchOrderPolygonDate();
  }, []);

  useEffect(() => {
    fetchPolygons();
  }, []);

  useEffect(() => {
    if (
      selectedDates.length === 2 &&
      selectedDates[0] &&
      selectedDates[1] &&
      polygonPath.length >= 3
    ) {
      const currentPayload = {
        startDate: selectedDates[0].toISOString(),
        endDate: selectedDates[1].toISOString(),
        polygon: polygonPath,
        INCLUDE_DELIVERY_FROM_STORE: includeDeliveryFromStore,
      };
      if (
        !prevPayloadRef.current ||
        !isEqual(prevPayloadRef.current, currentPayload)
      ) {
        setMapLoading(true);
        fetchMapData(currentPayload, true);
        prevPayloadRef.current = currentPayload;
      }
    }
  }, [selectedDates, polygonPath, includeDeliveryFromStore]);

  useEffect(() => {
    if (showAllStores && polygonPath.length >= 3) {
      fetchAllStores();
    } else if (!showAllStores) {
      setAllStoresData([]);
      setAllStoresMap([]);
    }
  }, [polygonPath, showAllStores]);

  const onLoad = (map) => {
    setMap(map);
  };

  const onPolygonComplete = (polygon) => {
    if (!polygon) return;

    const path = polygon
      .getPath()
      .getArray()
      .map((latLng) => ({
        latitude: latLng.lat(),
        longitude: latLng.lng(),
      }));
    setPolygonPath(path);
    setDrawing(false);
  };

  const handleDateChange = (dates) => {
    if (!dates) return;

    const start = dates[0];
    const end = dates[1];
    const [prevStart, prevEnd] = selectedDates;

    if (prevStart && !start.isSame(prevStart, 'day')) {
      setSelectedDates([start, null]);
      return;
    }

    if (prevEnd && !end.isSame(prevEnd, 'day')) {
      setSelectedDates([prevStart, end]);
    }

    if (start && end && end.diff(start, 'days') > maxDays) {
      notification.error({
        message: t('error'),
        description: `${t('maxWeeksAllowed')}: ${maxDays / 7} hafta`,
      });
      setSelectedDates([start, null]);
      return;
    }

    setSelectedDates(dates);
  };

  const disabledDate = (current) => {
    if (!current) return false;

    if (current > moment().endOf('day')) return true;

    if (selectedDates.length === 1) {
      const startDate = selectedDates[0];
      const minDate = startDate.clone().subtract(maxDays, 'days');
      const maxDate = startDate.clone().add(maxDays, 'days');
      return current < minDate || current > maxDate;
    }

    return false;
  };

  const fetchPolygons = async () => {
    setLoading(true);
    const response = await orderPolygonDetail();
    const fetchedPolygons = response.content || [];
    const validPolygons = fetchedPolygons.filter(
      (p) => p && typeof p.name === 'string'
    );
    setPolygons(validPolygons);
    setLoading(false);
  };

  const fetchMapData = async (payload, includeFilters = false) => {
    if (!payload.polygon || !payload.startDate || !payload.endDate) return;

    const detailPayload = includeFilters ? { ...payload, ...filters } : payload;

    const currentStoreFilters = storeFiltersRef.current;
    const storeFilterPayload = {};
    if (currentStoreFilters.type !== undefined && currentStoreFilters.type !== null)
      storeFilterPayload.type = currentStoreFilters.type;
    if (
      currentStoreFilters.ecomStatus !== undefined &&
      currentStoreFilters.ecomStatus !== null
    )
      storeFilterPayload.ecomStatus = currentStoreFilters.ecomStatus;
    if (
      currentStoreFilters.active !== undefined &&
      currentStoreFilters.active !== null
    )
      storeFilterPayload.active = currentStoreFilters.active;
    if (
      currentStoreFilters.minM2 !== undefined &&
      currentStoreFilters.minM2 !== null
    )
      storeFilterPayload.minM2 = currentStoreFilters.minM2;
    if (
      currentStoreFilters.maxM2 !== undefined &&
      currentStoreFilters.maxM2 !== null
    )
      storeFilterPayload.maxM2 = currentStoreFilters.maxM2;

    setMapLoading(true);
    try {
      const response = await orderMapDetail({
        ...detailPayload,
        ...storeFilterPayload,
      });
      setSummaryData(response.orderSummary);
      setOrderDetails(response.orderDetail || []);
    } catch (error) {
      setSummaryData(null);
      setOrderDetails([]);
    } finally {
      setMapLoading(false);
    }
  };

  const fetchAllStores = async () => {
    if (polygonPath.length < 3) return;

    setMapLoading(true);
    try {
      const payload = {
        polygon: polygonPath,
      };
      const response = await orderMapStoresPolygon(payload);
      setAllStoresMap(response);
      const stores = response.map((store) => ({
        storeCode: store.storeCode,
        storeName: store.storeName,
        orderCount: 0,
      }));
      setAllStoresData(stores);
    } catch (error) {
      setAllStoresData([]);
      setAllStoresMap([]);
    } finally {
      setMapLoading(false);
    }
  };

  const handleFilterChange = (key) => (e) => {
    setFilters((prev) => ({
      ...prev,
      [key]: e.target.checked,
    }));
  };

  const resetFilters = () => {
    setFilters({
      IS_CANCELLED_ORDER: false,
      IS_RETURNED_ORDER: false,
      IS_NOT_ON_TIME_ORDER: false,
      IS_ON_TIME_ORDER: false,
      IS_NOT_RETURNED_ORDER: false,
    });
  };

  const handleResetFilters = () => {
    resetFilters();
    if (
      selectedDates.length === 2 &&
      selectedDates[0] &&
      selectedDates[1] &&
      polygonPath.length >= 3
    ) {
      const payload = {
        startDate: selectedDates[0].toISOString(),
        endDate: selectedDates[1].toISOString(),
        polygon: polygonPath,
        INCLUDE_DELIVERY_FROM_STORE: includeDeliveryFromStore,
      };
      fetchMapData(payload, false);
      prevPayloadRef.current = payload;
    }
  };

  const handleListFilteredData = () => {
    if (
      selectedDates.length === 2 &&
      selectedDates[0] &&
      selectedDates[1] &&
      polygonPath.length >= 3
    ) {
      const payload = {
        startDate: selectedDates[0].toISOString(),
        endDate: selectedDates[1].toISOString(),
        polygon: polygonPath,
        INCLUDE_DELIVERY_FROM_STORE: includeDeliveryFromStore,
      };
      fetchMapData(payload, true);
      prevPayloadRef.current = payload;
    }
  };

  const handleToggleAllStores = () => {
    setShowAllStores((prev) => !prev);
  };

  const handleApplyStoreFilter = (values) => {
    storeFiltersRef.current = values;
    setStoreFilters(values);
    setIsStoreFilterOpen(false);

    if (
      selectedDates.length === 2 &&
      selectedDates[0] &&
      selectedDates[1] &&
      polygonPath.length >= 3
    ) {
      const payload = {
        startDate: selectedDates[0].toISOString(),
        endDate: selectedDates[1].toISOString(),
        polygon: polygonPath,
        INCLUDE_DELIVERY_FROM_STORE: includeDeliveryFromStore,
      };
      setMapLoading(true);
      fetchMapData(payload, true);
      prevPayloadRef.current = payload;
    }
  };

  const notificationSave = (message, description) => {
    notification.success({
      message: message,
      description: description,
    });
  };

  const handleSavePolygon = async () => {
    if (
      !selectedDates ||
      selectedDates.length !== 2 ||
      !polygonPath ||
      polygonPath.length === 0 ||
      !polygonName.trim()
    ) {
      return;
    }

    setLoading(true);
    try {
      const startDate = selectedDates[0].toISOString();
      const endDate = selectedDates[1].toISOString();

      const payload = {
        name: polygonName,
        startDate,
        endDate,
        polygon: polygonPath,
      };

      const response = await orderPolygonSave(payload);
      const newPolygon = response.data;
      const res = await orderPolygonDetail();
      const fetchedPolygons = res.content || [];
      const validPolygons = fetchedPolygons.filter(
        (p) => p && typeof p.name === 'string'
      );
      setPolygons(validPolygons);

      const addedPolygon = validPolygons.find((p) => p.name === polygonName);
      if (addedPolygon) {
        setSelectedPolygon(addedPolygon);
        setPolygonPath(addedPolygon.polygon);
      } else {
        setSelectedPolygon(newPolygon);
        setPolygonPath(newPolygon.polygon);
      }
      setPolygonName('');
      setIsAddModalVisible(false);
      notificationSave(t('processCompleted'), t('addedNewPolygon'));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePolygonSelect = (polygonName) => {
    const polygon = polygons.find((p) => p.name === polygonName);
    if (!polygon) return;

    clearCoordinates();
    resetFilters();
    setSelectedPolygon(polygon);
    setPolygonPath(polygon.polygon);
    setSelectedDates([moment(polygon.startDate), moment(polygon.endDate)]);
    const payload = {
      startDate: moment(polygon.startDate).toISOString(),
      endDate: moment(polygon.endDate).toISOString(),
      polygon: polygon.polygon,
      INCLUDE_DELIVERY_FROM_STORE: includeDeliveryFromStore,
    };
    fetchMapData(payload, false);
    prevPayloadRef.current = payload;
  };

  const handleEditPolygon = async () => {
    if (!selectedPolygon || !editPolygonName.trim()) {
      return;
    }
    setLoading(true);

    try {
      const updatedPolygon = {
        name: editPolygonName,
        startDate: selectedDates[0].toISOString(),
        endDate: selectedDates[1].toISOString(),
        polygon: polygonPath,
      };

      await orderPolygonEdit(updatedPolygon, selectedPolygon.id);
      const res = await orderPolygonDetail();
      const fetchedPolygons = res.content || [];
      const validPolygons = fetchedPolygons.filter(
        (p) => p && typeof p.name === 'string'
      );
      setPolygons(validPolygons);
      const updatedSelectedPolygon = validPolygons.find(
        (p) => p.id === selectedPolygon.id
      );
      if (updatedSelectedPolygon) {
        setSelectedPolygon(updatedSelectedPolygon);
      } else {
        setSelectedPolygon(null);
      }
      setIsEditModalVisible(false);
      notificationSave(t('processCompleted'), t('updatedNewPolygon'));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePolygon = () => {
    if (!selectedPolygon) return;

    Modal.confirm({
      title: t('modalDeleteTitle'),
      content: `${selectedPolygon.name} ${t('polygonWillDelete')}`,
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      width: 500,
      centered: true,
      onOk: () => {
        return new Promise(async (resolve, reject) => {
          setLoading(true);
          try {
            await orderPolygonDelete(selectedPolygon.id);
            await fetchPolygons();
            clearCoordinates();
            setSelectedDates([]);
            notificationSave(t('processCompleted'), t('polygonDeleted'));
            resolve();
          } catch (error) {
            reject(error);
          } finally {
            setLoading(false);
          }
        });
      },
    });
  };

  const clearCoordinates = () => {
    setPolygonPath([]);
    setSelectedPolygon(null);
    setSummaryData(null);
    setOrderDetails([]);
    setDrawing(true);
    resetFilters();
    setAllStoresData([]);
    setAllStoresMap([]);
    setShowAllStores(false);
    prevPayloadRef.current = null;
    setStoreFilters({});
    storeFiltersRef.current = {};
    storeFilterForm.resetFields();
  };

  const showAddModal = () => {
    setPolygonName('');
    setIsAddModalVisible(true);
  };

  const isAddButtonDisabled = () => {
    if (
      !selectedDates.length ||
      selectedDates.length < 2 ||
      !selectedDates[0] ||
      !selectedDates[1] ||
      polygonPath.length < 3
    ) {
      return true;
    }

    if (!selectedPolygon) return false;

    const datesUnchanged =
      selectedDates[0].isSame(moment(selectedPolygon.startDate), 'day') &&
      selectedDates[1].isSame(moment(selectedPolygon.endDate), 'day');
    const coordsUnchanged =
      JSON.stringify(polygonPath) === JSON.stringify(selectedPolygon.polygon);

    return datesUnchanged && coordsUnchanged;
  };

  const filteredAllStoresData = allStoresData.filter((store) => {
    const storeMapInfo = allStoresMap?.find(
      (s) => s.storeCode === store.storeCode,
    );
    if (!storeMapInfo) return true;
    if (
      storeFilters.type !== undefined &&
      storeFilters.type !== null &&
      storeMapInfo.storeType !== storeFilters.type
    ) {
      return false;
    }
    if (
      storeFilters.ecomStatus !== undefined &&
      storeFilters.ecomStatus !== null &&
      storeMapInfo.ecomStatus !== storeFilters.ecomStatus
    ) {
      return false;
    }
    if (
      storeFilters.active !== undefined &&
      storeFilters.active !== null &&
      storeMapInfo.active !== storeFilters.active
    ) {
      return false;
    }
    return true;
  });

  const filteredAllStoresMap = allStoresMap.filter((store) => {
    if (
      storeFilters.type !== undefined &&
      storeFilters.type !== null &&
      store.storeType !== storeFilters.type
    ) {
      return false;
    }
    if (
      storeFilters.ecomStatus !== undefined &&
      storeFilters.ecomStatus !== null &&
      store.ecomStatus !== storeFilters.ecomStatus
    ) {
      return false;
    }
    if (
      storeFilters.active !== undefined &&
      storeFilters.active !== null &&
      store.active !== storeFilters.active
    ) {
      return false;
    }
    return true;
  });

  const combinedStoreData = showAllStores
    ? [
        ...(summaryData?.storeOrderSummaryList || []),
        ...filteredAllStoresData.filter(
          (store) =>
            !summaryData?.storeOrderSummaryList?.some(
              (s) => s.storeCode === store.storeCode
            )
        ),
      ]
    : summaryData?.storeOrderSummaryList || [];

  return (
    <>
      <Row gutter={30}>
        <Col span={8}>
          {isCrudEnabled && (
            <Space>
              <Button
                type="primary"
                shape="round"
                title={t('addPolygon')}
                icon={<PlusOutlined />}
                onClick={showAddModal}
                disabled={isAddButtonDisabled()}
              />
              <Button
                shape="round"
                title={t('editPolygon')}
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  if (selectedPolygon) {
                    setEditPolygonName(selectedPolygon.name);
                    setIsEditModalVisible(true);
                  }
                }}
                disabled={!selectedPolygon}
              />
              <Button
                danger
                shape="round"
                title={t('slotManagement.delete')}
                icon={<DeleteOutlined />}
                onClick={handleDeletePolygon}
                disabled={!selectedPolygon}
              />
              <ExportOrderDetailData
                payload={
                  selectedDates.length === 2 && selectedDates[0] && selectedDates[1] && polygonPath.length >= 3
                    ? {
                        startDate: selectedDates[0].toISOString(),
                        endDate: selectedDates[1].toISOString(),
                        polygon: polygonPath,
                        INCLUDE_DELIVERY_FROM_STORE: includeDeliveryFromStore,
                        ...filters,
                        ...(storeFilters.type != null ? { type: storeFilters.type } : {}),
                        ...(storeFilters.ecomStatus != null ? { ecomStatus: storeFilters.ecomStatus } : {}),
                        ...(storeFilters.active != null ? { active: storeFilters.active } : {}),
                        ...(storeFilters.minM2 != null ? { minM2: storeFilters.minM2 } : {}),
                        ...(storeFilters.maxM2 != null ? { maxM2: storeFilters.maxM2 } : {}),
                      }
                    : null
                }
                disabled={loading || mapLoading || orderDetails.length === 0}
              />
            </Space>
          )}
          <Select
            placeholder={t('selectPolygon')}
            onChange={handlePolygonSelect}
            style={{ width: '100%', marginTop: 16 }}
            value={selectedPolygon?.name || undefined}
            loading={loading}
            allowClear={false}
            disabled={loading || mapLoading}
          >
            {polygons
              .filter((polygon) => polygon && typeof polygon.name === 'string')
              .map((polygon) => (
                <Option key={polygon.name} value={polygon.name}>
                  {polygon.name}
                </Option>
              ))}
          </Select>

          <RangePicker
            onChange={handleDateChange}
            value={selectedDates}
            placeholder={[t('validityStartDate'), t('validityEndDate')]}
            format="DD.MM.YYYY"
            allowClear={false}
            disabledDate={disabledDate}
            style={{ width: '100%', marginTop: 16 }}
          />

          {summaryData && (
            <div>
              <div style={{ marginTop: 16 }}>
                <Card
                  title={t('polygonSummary')}
                  extra={
                    <Space>
                      <Button onClick={handleResetFilters}>{t('clear')}</Button>
                      <Button type="primary" onClick={handleListFilteredData}>
                        {t('show')}
                      </Button>
                    </Space>
                  }
                >
                  <List
                    dataSource={[
                      {
                        label: t('totalOrders'),
                        value: summaryData.totalOrderCount,
                        key: 'totalOrders',
                      },
                      {
                        label: t('returnedOrders'),
                        value: summaryData.hasNotReturnedOrderCount,
                        key: 'IS_NOT_RETURNED_ORDER',
                      },
                      {
                        label: t('notReturnedOrders'),
                        value: summaryData.hasReturnedOrderCount,
                        key: 'IS_RETURNED_ORDER',
                      },
                      {
                        label: t('onTimeOrders'),
                        value: summaryData.onTimeOrderCount,
                        key: 'IS_ON_TIME_ORDER',
                      },
                      {
                        label: t('notOnTimeOrders'),
                        value: summaryData.notOnTimeOrderCount,
                        key: 'IS_NOT_ON_TIME_ORDER',
                      },
                      {
                        label: t('cancelledOrders'),
                        value: summaryData.cancelledOrderCount,
                        key: 'IS_CANCELLED_ORDER',
                      },
                    ]}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            item.key !== 'totalOrders' ? (
                              <Space>
                                <Checkbox
                                  id={`checkbox-${item.key}`}
                                  checked={filters[item.key]}
                                  onChange={handleFilterChange(item.key)}
                                  disabled={mapLoading}
                                />
                                <label
                                  htmlFor={`checkbox-${item.key}`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {item.label}
                                </label>
                              </Space>
                            ) : (
                              item.label
                            )
                          }
                        />
                        <div>{item.value !== null ? item.value : '--'}</div>
                      </List.Item>
                    )}
                  />
                </Card>
              </div>

              <Card
                title={t('storeDetails')}
                style={{ marginTop: 16, maxHeight: '400px', overflowY: 'auto' }}
                loading={mapLoading}
                extra={
                  <Space>
                    <Button
                      type={showAllStores ? 'default' : 'primary'}
                      onClick={handleToggleAllStores}
                      disabled={polygonPath.length < 3 || mapLoading}
                    >
                      {showAllStores ? t('hideAllStores') : t('showAllStores')}
                    </Button>
                    <Badge
                      count={[
                        storeFilters.type !== undefined && storeFilters.type !== null,
                        storeFilters.ecomStatus !== undefined &&
                          storeFilters.ecomStatus !== null,
                        storeFilters.active !== undefined &&
                          storeFilters.active !== null,
                        storeFilters.minM2 !== undefined &&
                          storeFilters.minM2 !== null,
                        storeFilters.maxM2 !== undefined &&
                          storeFilters.maxM2 !== null,
                      ].filter(Boolean).length}
                      size="small"
                    >
                      <Button
                        icon={<FilterOutlined />}
                        onClick={() => setIsStoreFilterOpen(true)}
                        disabled={polygonPath.length < 3 || mapLoading}
                      />
                    </Badge>
                  </Space>
                }
              >
                <Table
                  dataSource={combinedStoreData}
                  columns={[
                    {
                      title: t('storeCode'),
                      dataIndex: 'storeCode',
                      key: 'storeCode',
                    },
                    {
                      title: t('storeName'),
                      dataIndex: 'storeName',
                      key: 'storeName',
                    },
                    {
                      title: t('orderCount'),
                      dataIndex: 'orderCount',
                      key: 'orderCount',
                    },
                  ]}
                  pagination={false}
                  rowKey="storeCode"
                />
              </Card>
            </div>
          )}
        </Col>

        <Col span={16}>
          <OrderAnalysisMap
            onLoad={onLoad}
            onPolygonComplete={onPolygonComplete}
            polygonPath={polygonPath}
            clearCoordinates={clearCoordinates}
            orderDetails={orderDetails}
            mapLoading={mapLoading}
            storeOrderSummaryList={combinedStoreData}
            allStoresMap={filteredAllStoresMap}
            showAllStores={showAllStores}
            drawing={drawing}
            onIncludeDeliveryFromStoreChange={setIncludeDeliveryFromStore}
          />
        </Col>
      </Row>

      <Modal
        title={t('addPolygon')}
        open={isAddModalVisible}
        onOk={handleSavePolygon}
        onCancel={() => setIsAddModalVisible(false)}
        okText={t('save')}
        cancelText={t('cancel')}
        confirmLoading={loading}
        centered
      >
        <Input
          placeholder={t('polygonName')}
          value={polygonName}
          onChange={(e) => setPolygonName(e.target.value)}
        />
      </Modal>

      <Modal
        title={t('editPolygon')}
        open={isEditModalVisible}
        onOk={handleEditPolygon}
        onCancel={() => setIsEditModalVisible(false)}
        okText={t('save')}
        cancelText={t('cancel')}
        confirmLoading={loading}
        centered
      >
        <Input
          value={editPolygonName}
          onChange={(e) => setEditPolygonName(e.target.value)}
          placeholder={t('polygonName')}
        />
      </Modal>

      <StoreDetailsFilter
        isOpen={isStoreFilterOpen}
        onClose={() => setIsStoreFilterOpen(false)}
        onApply={handleApplyStoreFilter}
        form={storeFilterForm}
      />
    </>
  );
};

export default OrderAnalysis;
